import moment from 'moment'
import { TripItem as FullTripItem } from 'tripPlanner/types/tripItem'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { DetailItemCardVariant } from 'tripPlanner/components/DetailItemCard/common/types'

export const PIN_COLORS = {
  MONDAY: '#363A45',
  TUESDAY: '#02B990',
  WEDNESDAY: '#E9AE6C',
  THURSDAY: '#8685E0',
  FRIDAY: '#BF9C88',
  SATURDAY: '#5DA6FF',
  SUNDAY: '#F48189',
} as const

export const SELECTED_PIN_COLORS = {
  MONDAY: '#000000',
  TUESDAY: '#00604A',
  WEDNESDAY: '#986934',
  THURSDAY: '#494891',
  FRIDAY: '#5C5439',
  SATURDAY: '#304DAB',
  SUNDAY: '#A02332',
} as const

export type DayOfWeek = keyof typeof PIN_COLORS

export const SAVED_ACTIVE_PIN_COLOR = '#FFFFFF'
export const SAVED_PIN_COLOR = '#EF505F'

type Trip = {
  items: Array<FullTripItem>
}

export function getPinColor(
  item: FullTripItem,
  active?: boolean,
): string {
  if (!item.startDate) {
    return active ? SAVED_PIN_COLOR : SAVED_ACTIVE_PIN_COLOR
  }

  const dayOfWeek = moment(item.startDate).format('dddd').toUpperCase() as DayOfWeek
  const colourSet = active ? SELECTED_PIN_COLORS : PIN_COLORS
  return colourSet[dayOfWeek] || colourSet.SUNDAY
}

export function getItemNumber(item: FullTripItem, trip?: Trip): number {
  if (!trip?.items) return 1

  const { items } = trip
  function sortByItineraryRank(a: FullTripItem, b: FullTripItem): number {
    return a.itineraryRankString.localeCompare(b.itineraryRankString)
  }

  function sortBySummaryRank(a: FullTripItem, b: FullTripItem): number {
    return a.summaryRankString.localeCompare(b.summaryRankString)
  }

  function getIndex(list: Array<FullTripItem>): number {
    return list.findIndex((tripItem) => tripItem.id === item.id) + 1
  }

  if (!item.startDate) {
    const undatedItems = items
      .filter(
        (tripItem) =>
          !tripItem.startDate &&
          !('alternativeGroup' in tripItem && tripItem.alternativeGroup),
      )
      .sort(sortBySummaryRank)

    return getIndex(undatedItems)
  }

  const itemDate = moment(item.startDate).format(ISO_DATE_FORMAT)

  const sameDayItems = items
    .filter(
      (tripItem) =>
        tripItem.startDate &&
        moment(tripItem.startDate).format(ISO_DATE_FORMAT) === itemDate,
    )
    .sort(sortByItineraryRank)

  return getIndex(sameDayItems)
}

export function getDayColor(date: moment.Moment, active?: boolean): string {
  const dayOfWeek = date.format('dddd').toUpperCase() as DayOfWeek
  const colorSet = active ? SELECTED_PIN_COLORS : PIN_COLORS
  return colorSet[dayOfWeek] ?? colorSet.SUNDAY
}

export function getItemPinBadge(
  item: FullTripItem,
  variant: DetailItemCardVariant,
  trip?: Trip,
): { itemNumber: number | undefined; itemColor: string | undefined } {
  if ((variant !== 'itinerary' && variant !== 'tripSummary') || !item.startDate) {
    return {
      itemNumber: undefined,
      itemColor: undefined,
    }
  }

  return {
    itemNumber: getItemNumber(item, trip),
    itemColor: getPinColor(item),
  }
}
